import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import React, { Dispatch, SetStateAction } from 'react';
import { t } from 'i18next';
import _ from 'lodash';
import { UseFormSetError } from 'react-hook-form';

import customerSetting from 'src/repositories/customerSetting';
import useQueryUrl from 'src/hooks/useQueryUrl';
import { PER, STATUS_CODE } from 'src/constants/app';

const apiCustomSettingPath = '/customers';
const apiSearchCustomerPath = '/customers/company';
const apiUpdateOccupationAndLocationPath = '/customers/occupation-location';
const apiUpdateUnchunSetteiPath = '/customers/unchin-settei';
const apiDefaultCustomerPath = '/customers/default';

type UseNotificationSetting = {
  setRowKeysSelected?: Dispatch<SetStateAction<React.Key[]>>;
  setError: UseFormSetError<any>;
};

const useCustomerSetting = ({
  setRowKeysSelected,
  setError,
}: UseNotificationSetting) => {
  const queryClient = useQueryClient();

  const queryCustomer = useQuery<
    AxiosResponse<Response.CustomerClassification>,
    AxiosError<Response.Error>
  >({
    queryKey: [apiCustomSettingPath],
    queryFn: customerSetting.getSearchCustomer,
  });

  const query = useQueryUrl<SearchParams.CustomerSetting>();
  const params = {
    page: query.page || 1,
    offset: query.offset || PER.DEFAULT,
    name: query.person_in_charge,
    email: query.email,
    faxNo: query.fax_number,
    telNo: query.phone_number,
    jusho: query.address,
    jushoCd: query.address_code,
    kokyakuKbnFlg: query.customer_classification ? 1 : undefined,
    kokyakuMei: query.company_name,
    kokyakuCd: query.customer_code,
    gyoushuId: query.job_id,
    shozaichiId: query.region_id,
  };

  const querySearchCustomer = useQuery<
    AxiosResponse<Response.CustomerSetting>,
    AxiosError<Response.Error>
  >({
    queryKey: [apiSearchCustomerPath, params],
    queryFn: customerSetting.getSearchCustomer,
  });

  const updateCurrency = useMutation<
    AxiosResponse<any>,
    AxiosError,
    Payload.UpdateCurrency
  >({
    mutationKey: [apiCustomSettingPath],
    mutationFn: (args) =>
      customerSetting.updateCurrency(
        `${apiCustomSettingPath}/${args.seqno}`,
        args,
      ),
    retry: 0,
    async onSuccess(res) {
      await queryClient.invalidateQueries([apiSearchCustomerPath, params]);
    },
    onError(error) {
      console.log(error.response);
    },
  });

  const deleteCustomerSetting = useMutation<
    AxiosResponse<any>,
    AxiosError,
    Payload.DeleteCustomerSettingItem
  >({
    mutationKey: [apiCustomSettingPath],
    mutationFn: (args) =>
      customerSetting.deleteCustomerSetting(apiCustomSettingPath, args),
    retry: 0,
    onSuccess: () => {
      queryClient.removeQueries([apiSearchCustomerPath]);
      setRowKeysSelected?.([]);
    },
    onError(err) {
      const errorCode = _.get(err, 'response.data.code');
      switch (errorCode) {
        case STATUS_CODE.DUPLICATE_DATA:
          return setError(t('message.E000060'), {
            type: 'custom',
          });
      }
    },
  });

  const updateOccupationAndLocation = useMutation<
    AxiosResponse<any>,
    AxiosError,
    Payload.UpdateOccupationAndLocation
  >({
    mutationKey: [apiUpdateOccupationAndLocationPath],
    mutationFn: (args) =>
      customerSetting.updateOccupationAndLocation(
        apiUpdateOccupationAndLocationPath,
        args,
      ),
    retry: 0,
    async onSuccess(res) {
      await queryClient.invalidateQueries([apiSearchCustomerPath, params]);
    },
    onError(error) {
      console.log(error.response);
    },
  });

  const updateUnchunSettei = useMutation<
    AxiosResponse<any>,
    AxiosError,
    Payload.UpdateUnchunSettei
  >({
    mutationKey: [apiUpdateUnchunSetteiPath],
    mutationFn: (args) =>
      customerSetting.updateUnchunSettei(apiUpdateUnchunSetteiPath, args),
    retry: 0,
    async onSuccess(res) {
      await queryClient.invalidateQueries([apiSearchCustomerPath, params]);
    },
    onError(error) {
      console.log(error.response);
    },
  });

  const setDefaultCustomer = useMutation<
    AxiosResponse<any>,
    AxiosError,
    Payload.SetDefaultCustomer
  >({
    mutationKey: [apiDefaultCustomerPath],
    mutationFn: (args) =>
      customerSetting.setDefaultCustomer(apiDefaultCustomerPath, args),
    retry: 0,
    async onSuccess(res) {
      await queryClient.invalidateQueries([apiSearchCustomerPath, params]);
      setError('', {
        type: 'custom',
      });
    },
    onError(err) {
      const errorCode = _.get(err, 'response.data.code');
      switch (errorCode) {
        case STATUS_CODE.DEFAULT_PROFIT_ALREADY_EXISTS:
          return setError(t('message.E000109'), {
            type: 'custom',
          });
      }
    },
  });

  const deleteDefaultCustomer = useMutation<
    AxiosResponse<any>,
    AxiosError,
    Payload.DeleteDefaultCustomer
  >({
    mutationKey: [apiDefaultCustomerPath],
    mutationFn: (args) =>
      customerSetting.deleteDefaultCustomer(apiDefaultCustomerPath, args),
    retry: 0,
    async onSuccess(res) {
      await queryClient.invalidateQueries([apiSearchCustomerPath, params]);
      setError('', {
        type: 'custom',
      });
    },
    onError(error) {
      console.log(error.response);
    },
  });

  return {
    queryCustomer,
    querySearchCustomer,
    updateCurrency,
    deleteCustomerSetting,
    updateOccupationAndLocation,
    updateUnchunSettei,
    setDefaultCustomer,
    deleteDefaultCustomer,
  };
};

export default useCustomerSetting;
